import { SALE_PAYMENT_METHODS } from "@artisancode/api-types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { useProducts } from "@/hooks/use-products";
import {
  useCreateRiderSale,
  useUpdateRiderSale,
} from "@/hooks/use-rider-sales";
import { useRiders } from "@/hooks/use-riders";
import { getErrorMessage } from "@/lib/error-message";
import { newIdempotencyKey } from "@/lib/idempotency";
import { queryKeys } from "@/lib/query-keys";
import { stockService } from "@/services/stock";

import {
  defaultRiderSaleValues,
  riderSaleSchema,
  type RiderSaleFormValues,
} from "./rider-sale-schema";
import {
  calculateTotalDiscrepancy,
  calculateTotalRemit,
  calculateTotalReturned,
  calculateTotalSold,
  filterNonZeroLines,
  todayIso,
} from "./rider-sale-utils";

import type { RiderSale } from "@artisancode/api-types";

export function useRiderSaleForm(
  open: boolean,
  editing: RiderSale | null,
  onSuccess: () => void,
) {
  const navigate = useNavigate();
  const { data: productsData } = useProducts({ per_page: 100 });
  const products = productsData?.items ?? [];
  const { data: ridersData } = useRiders({ is_active: true, per_page: 100 });
  const riders = ridersData?.items ?? [];
  const { mutate: create, isPending: isCreating } = useCreateRiderSale();
  const { mutate: update, isPending: isUpdating } = useUpdateRiderSale();
  // One key per opened form: a retry after a network error reuses it, so the
  // server returns the rider sale it already booked instead of dispatching twice.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const idempotencyKey = useMemo(() => newIdempotencyKey("rsale"), [open]);

  const { data: stocksData } = useQuery({
    queryKey: queryKeys.stocks.list({ type: "product" }),
    queryFn: () =>
      stockService.list({
        type: "product",
        page: 1,
        per_page: 200,
      }),
    enabled: open,
  });

  const stockByProductId = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of stocksData?.items ?? []) {
      map.set(item.itemId, item.onHandQty);
    }
    return map;
  }, [stocksData]);

  const form = useForm<RiderSaleFormValues>({
    resolver: zodResolver(riderSaleSchema),
    defaultValues: defaultRiderSaleValues,
  });

  const { fields } = useFieldArray({ control: form.control, name: "lines" });
  const {
    fields: paymentFields,
    append: appendPayment,
    remove: removePayment,
  } = useFieldArray({ control: form.control, name: "payments" });
  const [productQuery, setProductQuery] = useState("");
  const visibleIndexes = fields
    .map((_, index) => index)
    .filter((index) =>
      (products[index]?.name ?? "")
        .toLowerCase()
        .includes(productQuery.toLowerCase()),
    );

  useEffect(() => {
    if (!open || products.length === 0) return;
    const existingByProduct = new Map(
      (editing?.lines ?? []).map((l) => [l.productId, l]),
    );
    form.reset({
      date: editing?.date ?? todayIso(),
      riderId: editing?.riderId ?? "",
      transactionCode: editing?.transactionCode ?? "",
      lines: products.map((p) => ({
        productId: p.id,
        dispatchedQty: existingByProduct.get(p.id)?.dispatchedQty ?? 0,
        soldQty: existingByProduct.get(p.id)?.soldQty ?? 0,
        returnedQty: existingByProduct.get(p.id)?.returnedQty ?? 0,
      })),
      payments: editing?.payments ?? [],
      status: editing?.status ?? "draft",
      notes: editing?.notes ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, products.length]);

  const watchedLines = useWatch({ control: form.control, name: "lines" });
  const totalJual = calculateTotalSold(watchedLines);
  const totalKembali = calculateTotalReturned(watchedLines);
  const totalSelisih = calculateTotalDiscrepancy(watchedLines);
  const totalSetoran = calculateTotalRemit(
    watchedLines,
    products.map((p) => p.sellingPrice),
  );

  const watchedPayments = useWatch({ control: form.control, name: "payments" });
  const usedMethods = (watchedPayments ?? []).map((p) => p?.method);
  const availableMethods = SALE_PAYMENT_METHODS.filter(
    (m) => !usedMethods.includes(m),
  );

  function addPayment() {
    const nextMethod = availableMethods[0];
    if (!nextMethod) return;
    appendPayment({ method: nextMethod, amount: 0 });
  }

  function methodOptionsFor(index: number) {
    const ownMethod = watchedPayments?.[index]?.method;
    return SALE_PAYMENT_METHODS.filter(
      (m) => m === ownMethod || !usedMethods.includes(m),
    );
  }

  function onSubmit(values: RiderSaleFormValues) {
    const payload = { ...values, lines: filterNonZeroLines(values.lines) };
    if (editing) {
      update(
        { id: editing.id, ...payload },
        {
          onSuccess: () => {
            toast.success("Penjualan rider berhasil diperbarui.");
            onSuccess();
          },
          onError: (err: Error) => {
            const message = getErrorMessage(
              err,
              "Gagal memperbarui penjualan rider.",
            );
            if (
              message.toLowerCase().includes("produksi") ||
              message.toLowerCase().includes("stok")
            ) {
              toast.error(message, {
                action: {
                  label: "Buat Produksi",
                  onClick: () => navigate("/production"),
                },
              });
            } else {
              toast.error(message);
            }
          },
        },
      );
    } else {
      create(
        { body: payload, idempotencyKey },
        {
          onSuccess: () => {
            toast.success("Penjualan rider berhasil ditambahkan.");
            onSuccess();
          },
          onError: (err: Error) => {
            const message = getErrorMessage(
              err,
              "Gagal menambahkan penjualan rider.",
            );
            if (
              message.toLowerCase().includes("produksi") ||
              message.toLowerCase().includes("stok")
            ) {
              toast.error(message, {
                action: {
                  label: "Buat Produksi",
                  onClick: () => navigate("/production"),
                },
              });
            } else {
              toast.error(message);
            }
          },
        },
      );
    }
  }

  return {
    form,
    fields,
    products,
    stockByProductId,
    riders,
    productQuery,
    setProductQuery,
    visibleIndexes,
    totalJual,
    totalKembali,
    totalSelisih,
    totalSetoran,
    paymentFields,
    addPayment,
    removePayment,
    methodOptionsFor,
    canAddPayment: availableMethods.length > 0,
    isSubmitting: isCreating || isUpdating,
    onSubmit: form.handleSubmit(onSubmit),
  };
}
